// src/features/feeds/components/FeedToolbar.tsx

import { Group, TextInput, Switch, Text, Button, ActionIcon } from '@mantine/core';
import {
  IconSearch,
  IconTrash,
  IconPlayerPlay,
  IconPlayerPause,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import { useSelection } from '@/shared/contexts/SelectionContext';
import type { SearchMode } from '@/lib/types/Media';

interface FeedToolbarProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  totalCount: number;
  resultCount: number;
  onBulkActivate: () => void;
  onBulkDeactivate: () => void;
  onBulkDelete: () => void;
  onCreateFeed: () => void;
  activateLoading?: boolean;
  deactivateLoading?: boolean;
  deleteLoading?: boolean;
}

export function FeedToolbar({
  searchText,
  onSearchChange,
  searchMode,
  onSearchModeChange,
  totalCount,
  resultCount,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
  onCreateFeed,
  activateLoading,
  deactivateLoading,
  deleteLoading,
}: FeedToolbarProps) {
  const { selectedCount, clearSelection } = useSelection();

  const hasSelection = selectedCount > 0;
  const isFiltered = searchText.trim().length > 0;

  return (
    <Group justify="space-between" py={8}>
      <Group>
        <TextInput
          placeholder="Search feeds..."
          leftSection={<IconSearch size={16} />}
          value={searchText}
          onChange={e => onSearchChange(e.currentTarget.value)}
          w={280}
        />

        <Switch
          label="Exact match"
          checked={searchMode === 'exact'}
          onChange={(e) => onSearchModeChange(e.currentTarget.checked ? 'exact' : 'fuzzy')}
          size="sm"
        />

        <Text size="sm" c="dimmed">
          {isFiltered
            ? `${resultCount} of ${totalCount} feeds`
            : `${totalCount} feeds`
          }
        </Text>
      </Group>

      {hasSelection ? (
        <Group gap="xs">
          <Text size="sm" fw={500}>
            {selectedCount} selected
          </Text>

          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={onBulkActivate}
            loading={activateLoading}
          >
            Activate
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPause size={14} />}
            onClick={onBulkDeactivate}
            loading={deactivateLoading}
          >
            Deactivate
          </Button>
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={onBulkDelete}
            loading={deleteLoading}
          >
            Delete
          </Button>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={clearSelection}
            aria-label="Clear selection"
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      ) : (
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={onCreateFeed}
        >
          Create Feed
        </Button>
      )}
    </Group>
  );
}
